import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  ClickAwayListener,
  LinearProgress,
  Portal,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import { format } from "date-fns";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getAdaptiveLogoFilter,
  resolveTeamLogoCandidates,
} from "../../../assets/teamBranding";
import {
  PlayerAvatarStack,
  PlayerDirectory,
  playerIdentity,
  playerLabel,
} from "../../../components/player/PlayerAvatar";
import { GameOutcome } from "../types/outcome";
import { Game, GameMetadata } from "../types/game";
import { TeamPollRank } from "../types/poll";
import { TeamRecords } from "../types/records";
import { Rules } from "../types/rules";
import { Team } from "../types/team";
import { scoringMagnitude } from "../utils/scoringDisplay";
import FlavorText from "./FlavorText";

export type MatchupCardState =
  | "open"
  | "selected"
  | "user-lock"
  | "picks-hidden"
  | "game-locked"
  | "live"
  | "correct"
  | "incorrect"
  | "push"
  | "no-pick"
  | "nail-hit";

export type MatchupCardExpansionMode = "natural" | "story-scroll";
export type MatchupCardCompletionSource = "league" | "user";

export interface MatchupCardProps {
  game: Game;
  metadata?: GameMetadata;
  homeTeam: Team;
  awayTeam: Team;
  state?: MatchupCardState;
  pickedTeamId?: number;
  onPick?: (teamId: number) => void;
  gameOfTheWeek?: boolean;
  onGameOfTheWeekClick?: () => void;
  showGameOfTheWeekButton?: boolean;
  currentUserLocked?: boolean;
  onToggleLock?: () => void;
  canLock?: boolean;
  lockCount?: number;
  maxLocks?: number;
  showLocks?: boolean;
  gameLocked?: boolean;
  revealSelections?: boolean;
  completion?: { completed: number; total: number };
  completionSource?: MatchupCardCompletionSource;
  distribution?: { home: number; away: number };
  homePickers?: string[];
  awayPickers?: string[];
  playerDirectory?: PlayerDirectory;
  currentUserEmail?: string;
  outcome?: GameOutcome;
  rules?: Rules;
  homeTeamPollRank?: TeamPollRank;
  awayTeamPollRank?: TeamPollRank;
  homeTeamRecord?: TeamRecords;
  awayTeamRecord?: TeamRecords;
  showFlavorText?: boolean;
  showLines?: boolean;
  showScoringLine?: boolean;
  showLiveLine?: boolean;
  selectedForAdmin?: boolean;
  onCardClick?: () => void;
  onEdit?: () => void;
  scoringLineLabel?: string;
  liveLineLabel?: string;
  expanded?: boolean;
  expansionMode?: MatchupCardExpansionMode;
  expandedHeight?: number | string;
  onExpandedChange?: (open: boolean) => void;
}

const stateLabels: Record<MatchupCardState, string> = {
  open: "Pick open",
  selected: "Your pick",
  "user-lock": "Pick locked",
  "picks-hidden": "Picks hidden",
  "game-locked": "Game locked - picks closed",
  live: "Live",
  correct: "Correct",
  incorrect: "Incorrect",
  push: "Push",
  "no-pick": "No pick",
  "nail-hit": "Nail hit",
};

export const deriveMatchupCardState = ({
  explicitState,
  game,
  pickedTeamId,
  currentUserLocked,
  gameLocked,
  outcome,
}: {
  explicitState?: MatchupCardState;
  game: Game;
  pickedTeamId?: number;
  currentUserLocked?: boolean;
  gameLocked?: boolean;
  outcome?: GameOutcome;
}): MatchupCardState => {
  if (outcome?.nail?.result === "hit") return "nail-hit";
  if (explicitState) return explicitState;
  if (outcome?.result === "push") return "push";
  if (outcome) {
    if (!pickedTeamId) return "no-pick";
    return outcome.winningTeamId === pickedTeamId ? "correct" : "incorrect";
  }
  if (!game.completed && Date.parse(game.time) <= Date.now()) return "live";
  if (gameLocked || game.completed) return "game-locked";
  if (currentUserLocked) return "user-lock";
  if (pickedTeamId) return "selected";
  return "open";
};

const formatSpread = (line?: number) => {
  if (line === undefined || Number.isNaN(line)) return "-";
  if (line === 0) return "PK";
  return line > 0 ? "+" + line : String(line);
};

const formatDirectionalSpread = (line?: number) => {
  if (line === undefined || Number.isNaN(line)) return "—";
  if (line === 0) return "• PK";
  return (line < 0 ? "◀ " : "▶ ") + formatSpread(line);
};

export const formatPickDeadline = (
  game: Pick<Game, "time" | "startTimeTBD" | "completed">,
  now = Date.now(),
) => {
  if (game.startTimeTBD) return "Time TBD";
  const kickoff = new Date(game.time);
  if (Number.isNaN(kickoff.getTime())) return "Unavailable";
  if (game.completed || kickoff.getTime() <= now) return "Closed";
  return format(kickoff, "EEE · h:mm a");
};

export const formatGameValue = ({
  rules,
  gameOfTheWeek = false,
  locked = false,
}: {
  rules?: Rules;
  gameOfTheWeek?: boolean;
  locked?: boolean;
}) => {
  if (!rules) return "—";
  const primary =
    scoringMagnitude(rules.gameScoring.bonus) +
    (gameOfTheWeek
      ? scoringMagnitude(rules.gameOfTheWeekRules?.bonus || 0)
      : 0);
  if (!locked || !rules.lockRules) return String(primary);
  const { bonus, penalty } = rules.lockRules.scoring;
  const lockBonus = scoringMagnitude(bonus);
  const lockPenalty = scoringMagnitude(penalty);
  if (lockBonus === lockPenalty) return `${primary} ± ${lockBonus}`;
  return `${primary} +${lockBonus} / −${lockPenalty}`;
};


export interface MatchupCardContext {
  primary: string;
  supporting: string;
}

export const getMatchupCardContext = ({
  state,
  game,
  gameValue,
  nailBonus,
  now,
}: {
  state: MatchupCardState;
  game: Game;
  gameValue: string;
  nailBonus?: number;
  now?: number;
}): MatchupCardContext => {
  const deadline = formatPickDeadline(game, now);
  switch (state) {
    case "open":
      return {
        primary: `Pick by ${deadline}`,
        supporting: "Choose either team to continue",
      };
    case "selected":
      return {
        primary: "Pick saved",
        supporting: `Editable until ${deadline}`,
      };
    case "user-lock":
      return {
        primary: "Lock active",
        supporting: `Game value ${gameValue}`,
      };
    case "picks-hidden":
    case "game-locked":
      return { primary: "Picks closed", supporting: "Selections are final" };
    case "live":
      return {
        primary: "Live now",
        supporting:
          game.period || game.clock
            ? `${game.period ? `Q${game.period}` : "Live"}${game.clock ? ` · ${game.clock}` : ""}`
            : "In progress",
      };
    case "correct":
      return {
        primary: "Correct pick",
        supporting: `Game value ${gameValue}`,
      };
    case "incorrect":
      return { primary: "Incorrect pick", supporting: "Final" };
    case "push":
      return { primary: "Push", supporting: "Excluded from accuracy" };
    case "no-pick":
      return { primary: "No pick submitted", supporting: "Final" };
    case "nail-hit":
      return {
        primary: "Nail hit",
        supporting: `Only correct picker${nailBonus === undefined ? "" : ` · +${nailBonus}`}`,
      };
  }
};

const bestRank = (ranks?: TeamPollRank) =>
  ranks?.length ? Math.min(...ranks.map((rank) => rank.rank)) : undefined;

const recordLabel = (records?: TeamRecords) =>
  records?.total
    ? [records.total.wins, records.total.losses, records.total.ties]
        .filter((_, index) => index < 2 || Boolean(records.total.ties))
        .join("-")
    : undefined;

const safeColor = (color?: string) =>
  /^#[0-9a-f]{6}$/i.test(color || "") ? color! : "#40564D";

const pickerCountLabel = (count: number, percent: number) =>
  `${count} ${count === 1 ? "player" : "players"} - ${percent}%`;


const TeamLogo = ({
  team,
  background,
  size = 52,
}: {
  team: Team;
  background: string;
  size?: number;
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode === "dark" ? "dark" : "light";
  const candidates = useMemo(
    () => resolveTeamLogoCandidates(team, mode),
    [team, mode],
  );
  const [index, setIndex] = useState(0);
  const key = candidates.join("|");

  useEffect(() => setIndex(0), [key]);

  if (!candidates[index]) {
    return (
      <Box
        role="img"
        aria-label={team.school + " logo unavailable; " + team.abbreviation}
        sx={{
          width: size,
          height: size,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(255,255,255,.92)",
          color: "#171D1A",
          fontWeight: 900,
          fontSize: 13,
          border: "2px solid rgba(0,0,0,.28)",
        }}
      >
        {team.abbreviation.slice(0, 4)}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={candidates[index]}
      alt={team.school + " logo"}
      onError={() => setIndex((value) => value + 1)}
      sx={{
        width: size,
        height: size,
        objectFit: "contain",
        filter: getAdaptiveLogoFilter(team.id, mode, background),
      }}
    />
  );
};

const outcomeBorder = (state: MatchupCardState, theme: Theme) => {
  if (state === "nail-hit") return theme.palette.nail.main;
  if (state === "correct") return theme.palette.scoring.main;
  if (state === "incorrect" || state === "no-pick") {
    return theme.palette.error.main;
  }
  if (state === "push") return theme.palette.warning.main;
  if (state === "live") return "#26A69A";
  if (state === "game-locked") return theme.palette.info.main;
  return theme.palette.divider;
};

const MatchupCard = ({
  game,
  metadata,
  homeTeam,
  awayTeam,
  state: explicitState,
  pickedTeamId,
  onPick,
  gameOfTheWeek = false,
  onGameOfTheWeekClick,
  showGameOfTheWeekButton = false,
  currentUserLocked = false,
  onToggleLock,
  canLock = false,
  lockCount = 0,
  maxLocks = 0,
  showLocks = false,
  gameLocked = false,
  revealSelections = false,
  completion,
  completionSource = "league",
  distribution,
  homePickers = [],
  awayPickers = [],
  playerDirectory,
  currentUserEmail,
  outcome,
  rules,
  homeTeamPollRank,
  awayTeamPollRank,
  homeTeamRecord,
  awayTeamRecord,
  showFlavorText = true,
  showScoringLine = true,
  showLiveLine = true,
  selectedForAdmin = false,
  onCardClick,
  onEdit,
  expanded,
  onExpandedChange,
  expansionMode = "natural",
  expandedHeight,
}: MatchupCardProps) => {
  const theme = useTheme();
  const cardAnchorRef = useRef<HTMLDivElement>(null);
  const detailsButtonRef = useRef<HTMLButtonElement>(null);
  const horizontalScrollRef = useRef<{
    element: HTMLElement;
    left: number;
  } | null>(null);
  const detailsId = useId();
  const [internalDetailsOpen, setInternalDetailsOpen] = useState(false);
  const detailsOpen = expanded ?? internalDetailsOpen;
  const [overlayPosition, setOverlayPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const updateDetailsOpen = useCallback(
    (open: boolean) => {
      if (expanded === undefined) setInternalDetailsOpen(open);
      onExpandedChange?.(open);
    },
    [expanded, onExpandedChange],
  );
  const line = metadata?.line ?? game.line;
  const flavor = metadata?.flavorText || "";
  const awayRank = bestRank(awayTeamPollRank);
  const homeRank = bestRank(homeTeamPollRank);
  const awayRecord = recordLabel(awayTeamRecord);
  const homeRecord = recordLabel(homeTeamRecord);
  const state = deriveMatchupCardState({
    explicitState,
    game,
    pickedTeamId,
    currentUserLocked,
    gameLocked,
    outcome,
  });
  const disabled =
    !onPick ||
    game.completed ||
    state === "live" ||
    state === "game-locked" ||
    state === "correct" ||
    state === "incorrect" ||
    state === "push" ||
    state === "no-pick" ||
    state === "nail-hit" ||
    currentUserLocked;

  const homeColor = safeColor(homeTeam.color);
  const awayColor = safeColor(awayTeam.color);
  const statusColor =
    state === "nail-hit"
      ? theme.palette.nail.main
      : state === "correct"
        ? theme.palette.scoring.main
        : state === "incorrect" || state === "no-pick"
          ? theme.palette.error.main
          : state === "live"
            ? "#C62828"
            : gameOfTheWeek
              ? theme.palette.gotw.main
              : state === "selected" || state === "user-lock"
                ? theme.palette.toggle.main
                : state === "game-locked"
                  ? theme.palette.lock.main
                  : theme.palette.surface.high;

  const statusForeground =
    statusColor === theme.palette.surface.high
      ? theme.palette.text.primary
      : state === "live"
        ? "#FFFFFF"
        : "#000000";
  const statusHeight = 24;
  const closedContentHeight = 150;
  const nailPlayer = outcome?.nail?.email
    ? playerIdentity(outcome.nail.email, playerDirectory)
    : undefined;
  const nailRecipient =
    state === "nail-hit" && nailPlayer ? playerLabel(nailPlayer) : undefined;
  const displayedGameValue = formatGameValue({
    rules,
    gameOfTheWeek,
    locked: currentUserLocked,
  });
  const context = getMatchupCardContext({
    state,
    game,
    gameValue: displayedGameValue,
    nailBonus: rules?.nailRules?.scoring.bonus,
  });
  const contextColor =
    state === "nail-hit"
      ? theme.palette.nail.main
      : state === "correct"
        ? theme.palette.scoring.main
        : state === "incorrect" || state === "no-pick"
          ? theme.palette.error.main
          : state === "push"
            ? theme.palette.warning.main
            : state === "live"
              ? theme.palette.error.main
              : state === "game-locked" || state === "picks-hidden"
                ? theme.palette.lock.main
                : state === "selected" || state === "user-lock"
                  ? theme.palette.toggle.main
                  : theme.palette.primary.main;
  const remainingLocks = Math.max(0, maxLocks - lockCount);
  const lockActionDisabled =
    !pickedTeamId ||
    (!currentUserLocked && (!canLock || lockCount >= maxLocks));
  const actionHelper = onEdit
    ? "Line and matchup story save independently"
    : showLocks && onToggleLock
      ? currentUserLocked
        ? `Lock active · Game value ${displayedGameValue}`
        : !pickedTeamId
          ? "Select a team first"
          : lockActionDisabled
            ? "All locks are already assigned"
            : `${remainingLocks} lock${remainingLocks === 1 ? "" : "s"} remaining`
      : showGameOfTheWeekButton && onGameOfTheWeekClick
        ? "One featured matchup per week"
        : undefined;
  const contextZoneColor = onEdit
    ? theme.palette.primary.main
    : showLocks && onToggleLock
      ? theme.palette.lock.main
      : showGameOfTheWeekButton && onGameOfTheWeekClick
        ? theme.palette.gotw.main
        : contextColor;

  const completed = Math.max(0, completion?.completed ?? 0);
  const total = Math.max(0, completion?.total ?? 0);
  const hasCompletion = Boolean(completion && total > 0);
  const isStoryScroll = detailsOpen && expansionMode === "story-scroll";
  const percent =
    total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const homePickerIdentities = homePickers.map((email) =>
    playerIdentity(email, playerDirectory),
  );
  const awayPickerIdentities = awayPickers.map((email) =>
    playerIdentity(email, playerDirectory),
  );
  const homePickerCount = distribution?.home ?? homePickers.length;
  const awayPickerCount = distribution?.away ?? awayPickers.length;
  const totalRevealed = homePickerCount + awayPickerCount;
  const homePercent = totalRevealed
    ? Math.round((homePickerCount / totalRevealed) * 100)
    : 0;
  const awayPercent = totalRevealed ? 100 - homePercent : 0;
  const nailWinningSide: "away" | "home" | undefined =
    state !== "nail-hit"
      ? undefined
      : outcome?.winningTeamId === awayTeam.id || outcome?.result === "away"
        ? "away"
        : outcome?.winningTeamId === homeTeam.id || outcome?.result === "home"
          ? "home"
          : undefined;

  const scoreVisible = game.completed || state === "live" || Boolean(outcome);
  const awayScore = outcome?.awayScore ?? game.awayPoints;
  const homeScore = outcome?.homeScore ?? game.homePoints;

  const renderTeam = (side: "away" | "home") => {
    const team = side === "away" ? awayTeam : homeTeam;
    const color = side === "away" ? awayColor : homeColor;
    const selected = pickedTeamId === team.id;
    const subdued = Boolean(pickedTeamId) && !selected && !scoreVisible;

    return (
      <ButtonBase
        disabled={disabled}
        onClick={() => onPick?.(team.id)}
        aria-label={"Pick " + team.school}
        aria-pressed={selected}
        sx={{
          position: "relative",
          width: "50%",
          height: 76,
          px: 1.25,
          display: "grid",
          placeItems: "center",
          color: theme.palette.getContrastText(color),
          bgcolor: color,
          opacity: subdued ? 0.58 : 1,
          boxShadow: selected
            ? "inset 0 0 0 3px " +
              (state === "nail-hit"
                ? theme.palette.nail.main
                : gameOfTheWeek
                  ? theme.palette.gotw.main
                  : theme.palette.toggle.main)
            : undefined,
          "&:focus-visible": {
            outline: "3px solid " + theme.palette.primary.main,
            outlineOffset: -3,
          },
        }}
      >
        <TeamLogo team={team} background={color} size={52} />
        {(selected || (currentUserLocked && selected)) && (
          <Box
            aria-label={currentUserLocked ? "Your locked pick" : "Your pick"}
            sx={{
              position: "absolute",
              top: 5,
              [side === "away" ? "left" : "right"]: 5,
              width: 20,
              height: 20,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(15,21,18,.82)",
              color: "#FFFFFF",
              border: "1px solid rgba(255,255,255,.75)",
            }}
          >
            {currentUserLocked ? (
              <LockOutlinedIcon sx={{ fontSize: 12 }} />
            ) : (
              <CheckRoundedIcon sx={{ fontSize: 14 }} />
            )}
          </Box>
        )}
      </ButtonBase>
    );
  };

  const rememberHorizontalScroll = useCallback(() => {
    let element = cardAnchorRef.current?.parentElement ?? null;
    while (element) {
      const overflowX = window.getComputedStyle(element).overflowX;
      if (
        ["auto", "scroll", "overlay"].includes(overflowX) &&
        element.scrollWidth > element.clientWidth
      ) {
        horizontalScrollRef.current = { element, left: element.scrollLeft };
        return;
      }
      element = element.parentElement;
    }
    horizontalScrollRef.current = null;
  }, []);

  const restoreHorizontalScroll = useCallback(() => {
    const position = horizontalScrollRef.current;
    if (position?.element.isConnected) {
      position.element.scrollLeft = position.left;
    }
  }, []);

  const updateOverlayPosition = useCallback(() => {
    const anchor = cardAnchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const minimumLeft = window.scrollX + 12;
    const maximumLeft = Math.max(
      minimumLeft,
      window.scrollX + window.innerWidth - 312 - 12,
    );
    setOverlayPosition({
      top: rect.top + window.scrollY,
      left: Math.min(
        Math.max(rect.left + window.scrollX, minimumLeft),
        maximumLeft,
      ),
    });
  }, []);

  useLayoutEffect(() => {
    if (detailsOpen) updateOverlayPosition();
  }, [detailsOpen, expansionMode, updateOverlayPosition]);

  useEffect(() => {
    if (!detailsOpen) return undefined;
    const handleResize = () => updateOverlayPosition();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [detailsOpen, updateOverlayPosition]);

  useLayoutEffect(() => {
    restoreHorizontalScroll();
  }, [detailsOpen, restoreHorizontalScroll]);

  const closeDetails = useCallback(() => {
    rememberHorizontalScroll();
    updateDetailsOpen(false);
    queueMicrotask(() => {
      restoreHorizontalScroll();
      detailsButtonRef.current?.focus({ preventScroll: true });
    });
  }, [rememberHorizontalScroll, restoreHorizontalScroll, updateDetailsOpen]);

  useEffect(() => {
    if (!detailsOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDetails();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [detailsOpen, closeDetails]);

  const cardSurface = (
    <Card
      component="article"
      data-testid="matchup-card"
      data-state={state}
      data-expanded={detailsOpen ? "true" : "false"}
      aria-label={
        awayTeam.school + " at " + homeTeam.school + ": " + stateLabels[state]
      }
      onClick={onCardClick}
      sx={{
        width: 312,
        minWidth: 312,
        maxWidth: 312,
        height: detailsOpen
          ? isStoryScroll
            ? (expandedHeight ??
              "min(720px, calc(100dvh - 24px - env(safe-area-inset-bottom)))")
            : "auto"
          : 576,
        minHeight: 576,
        display: "grid",
        gridTemplateRows:
          statusHeight +
          "px 76px " +
          (scoreVisible ? "42px " : "0px ") +
          (isStoryScroll
            ? "minmax(0, 1fr) "
            : detailsOpen
              ? "auto "
              : closedContentHeight + "px ") +
          "64px " +
          (isStoryScroll ? "76px " : "minmax(76px, 1fr) ") +
          "138px",
        overflow: "hidden",
        border: selectedForAdmin || state !== "open" ? 2 : 1,
        borderColor: selectedForAdmin
          ? "primary.main"
          : outcomeBorder(state, theme),
        borderRadius: "12px",
        bgcolor: "surface.container",
        cursor: onCardClick ? "pointer" : "default",
        boxShadow: detailsOpen
          ? 16
          : gameOfTheWeek
            ? "0 4px 14px rgba(0,0,0,.2)"
            : undefined,
        flex: "0 0 auto",
        scrollSnapAlign: "start",
      }}
    >
      <Box
        data-testid="matchup-status-row"
        sx={{
          height: statusHeight,
          px: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: statusColor,
          color: statusForeground,
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
          {(state === "picks-hidden" || state === "game-locked") && (
            <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} />
          )}
          {(state === "user-lock" || state === "game-locked") && (
            <LockOutlinedIcon sx={{ fontSize: 15 }} />
          )}
          <Typography
            variant="overline"
            sx={{
              color: "inherit",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {state === "nail-hit" && nailRecipient
              ? `${stateLabels[state]} · ${nailRecipient}`
              : stateLabels[state]}
          </Typography>
        </Stack>
        {gameOfTheWeek && (
          <Stack direction="row" spacing={0.25} alignItems="center">
            <StarRoundedIcon sx={{ fontSize: 15 }} />
            <Typography variant="overline" sx={{ color: "inherit" }}>
              {state.startsWith("nail") ? "Nail + GOTW" : "Game of the Week"}
            </Typography>
          </Stack>
        )}
      </Box>

      <Box sx={{ position: "relative", display: "flex", height: 76 }}>
        {renderTeam("away")}
        {renderTeam("home")}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 26,
            height: 26,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(15,21,18,.9)",
            color: "#FFFFFF",
            border: "1px solid rgba(255,255,255,.6)",
            fontSize: 10,
            fontWeight: 900,
          }}
        >
          @
        </Box>
      </Box>

      <Box
        aria-label={
          scoreVisible
            ? awayTeam.school +
              " " +
              (awayScore ?? "—") +
              ", " +
              homeTeam.school +
              " " +
              (homeScore ?? "—")
            : undefined
        }
        sx={{
          height: scoreVisible ? 42 : 0,
          display: "grid",
          gridTemplateColumns: "1fr 48px 1fr",
          alignItems: "center",
          textAlign: "center",
          overflow: "hidden",
          bgcolor: "surface.lowest",
          borderBottom: scoreVisible ? 1 : 0,
          borderColor: "divider",
        }}
      >
        <Typography sx={{ fontSize: 24, fontWeight: 900 }}>
          {awayScore ?? "—"}
        </Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 900 }}>–</Typography>
        <Typography sx={{ fontSize: 24, fontWeight: 900 }}>
          {homeScore ?? "—"}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 1.5,
          pt: 1.25,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Typography
          align="center"
          sx={{ fontSize: 13, lineHeight: "18px", fontWeight: 800 }}
        >
          {(awayRank ? "#" + awayRank + " " : "") +
            awayTeam.school +
            " @ " +
            (homeRank ? "#" + homeRank + " " : "") +
            homeTeam.school}
        </Typography>
        <Typography
          align="center"
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.25 }}
        >
          {game.startTimeTBD
            ? format(new Date(game.time), "EEE, MMM d") + " · Time TBD"
            : format(new Date(game.time), "EEE, MMM d - h:mm a")}
          {state === "live" && game.period
            ? " - Q" + game.period + " " + (game.clock || "")
            : ""}
        </Typography>
        {(awayRecord || homeRecord) && (
          <Stack direction="row" justifyContent="space-between" px={0.5}>
            <Typography variant="caption">
              {awayRecord || "Record unavailable"}
            </Typography>
            <Typography variant="caption">
              {homeRecord || "Record unavailable"}
            </Typography>
          </Stack>
        )}
        <Box
          id={detailsOpen ? detailsId : undefined}
          role={detailsOpen ? "region" : undefined}
          aria-label={detailsOpen ? "Full matchup story" : undefined}
          tabIndex={isStoryScroll ? 0 : undefined}
          sx={{
            mt: 0.5,
            minHeight: 38,
            minWidth: 0,
            flex: isStoryScroll ? "1 1 auto" : undefined,
            overflowY: isStoryScroll ? "auto" : "visible",
            overscrollBehavior: isStoryScroll ? "contain" : undefined,
            WebkitOverflowScrolling: isStoryScroll ? "touch" : undefined,
            pr: isStoryScroll ? 0.5 : 0,
            scrollbarWidth: isStoryScroll ? "thin" : undefined,
            scrollbarColor: isStoryScroll
              ? alpha(theme.palette.primary.main, 0.65) + " transparent"
              : undefined,
            "&::-webkit-scrollbar": { width: isStoryScroll ? 4 : 0 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.primary.main, 0.65),
              borderRadius: 99,
            },
          }}
        >
          {showFlavorText && flavor ? (
            <FlavorText text={flavor} preview={!detailsOpen} />
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ fontStyle: "italic", lineHeight: "19px" }}
            >
              Make your call before kickoff.
            </Typography>
          )}
        </Box>
        <ButtonBase
          ref={detailsButtonRef}
          aria-expanded={detailsOpen}
          aria-controls={detailsOpen ? detailsId : undefined}
          onClick={(event) => {
            event.stopPropagation();
            if (detailsOpen) {
              closeDetails();
              return;
            }
            updateOverlayPosition();
            rememberHorizontalScroll();
            updateDetailsOpen(true);
          }}
          disabled={!flavor}
          sx={{
            mt: "auto",
            mx: -1.5,
            minHeight: 32,
            px: 1.5,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            color: flavor ? "primary.main" : "text.disabled",
          }}
        >
          <Typography variant="caption" fontWeight={700}>
            Flavor details - {Array.from(flavor).length} / 1,500
          </Typography>
          <Stack direction="row" spacing={0.25} alignItems="center">
            <Typography variant="caption" fontWeight={700}>
              {detailsOpen ? "Read less" : "Read more"}
            </Typography>
            {detailsOpen ? (
              <KeyboardArrowUpRoundedIcon sx={{ fontSize: 16 }} />
            ) : (
              <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />
            )}
          </Stack>
        </ButtonBase>
      </Box>

      <Box
        sx={{
          px: 1.5,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          alignItems: "center",
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {revealSelections ? (
          <>
            <Stack spacing={0.25} alignItems="flex-start">
              <PlayerAvatarStack
                players={awayPickerIdentities}
                teamName={awayTeam.school}
              />
              <Typography
                variant="caption"
                color={
                  nailWinningSide === "away"
                    ? theme.palette.mode === "light"
                      ? "nail.dark"
                      : "nail.main"
                    : undefined
                }
                fontWeight={nailWinningSide === "away" ? 800 : undefined}
              >
                {pickerCountLabel(awayPickerCount, awayPercent)}
              </Typography>
            </Stack>
            <Stack spacing={0.25} alignItems="flex-end">
              <PlayerAvatarStack
                players={homePickerIdentities}
                teamName={homeTeam.school}
                align="flex-end"
              />
              <Typography
                variant="caption"
                color={
                  nailWinningSide === "home"
                    ? theme.palette.mode === "light"
                      ? "nail.dark"
                      : "nail.main"
                    : undefined
                }
                fontWeight={nailWinningSide === "home" ? 800 : undefined}
              >
                {pickerCountLabel(homePickerCount, homePercent)}
              </Typography>
            </Stack>
          </>
        ) : (
          <Box sx={{ gridColumn: "1 / -1" }}>
            {hasCompletion ? (
              <>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" fontWeight={700}>
                    {completionSource === "user"
                      ? "Your weekly progress"
                      : "League completion"}
                  </Typography>
                  <Typography variant="caption">
                    {completed + " / " + total}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={percent}
                  aria-label={
                    completed +
                    " of " +
                    total +
                    (completionSource === "user"
                      ? " weekly picks completed"
                      : " league picks submitted; selections hidden until kickoff")
                  }
                  sx={{ mt: 0.75, height: 4, borderRadius: 2 }}
                />
              </>
            ) : (
              <Typography variant="caption" fontWeight={700}>
                {pickedTeamId
                  ? "Your pick is saved"
                  : "Selections stay hidden until kickoff"}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Box
        data-testid="matchup-context-zone"
        sx={{
          px: 1.5,
          py: 1.25,
          minHeight: 76,
          display: "flex",
          alignItems: "center",
          borderLeft: 3,
          borderColor: contextZoneColor,
          bgcolor: alpha(
            contextZoneColor,
            theme.palette.mode === "light" ? 0.08 : 0.14,
          ),
        }}
      >
        <Stack
          width="100%"
          minHeight={0}
          spacing={0.75}
          alignItems="center"
          justifyContent="center"
        >
          {onEdit ? (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<EditRoundedIcon />}
              onClick={(event) => {
                event.stopPropagation();
                onEdit();
              }}
            >
              Edit game details
            </Button>
          ) : showLocks && onToggleLock ? (
            <Button
              fullWidth
              variant={currentUserLocked ? "contained" : "outlined"}
              startIcon={<LockOutlinedIcon />}
              onClick={(event) => {
                event.stopPropagation();
                onToggleLock();
              }}
              disabled={lockActionDisabled}
            >
              {currentUserLocked ? "Locked - bonus active" : "Add lock"}
            </Button>
          ) : showGameOfTheWeekButton && onGameOfTheWeekClick ? (
            <Button
              fullWidth
              color="gotw"
              variant={gameOfTheWeek ? "contained" : "outlined"}
              startIcon={<StarRoundedIcon />}
              onClick={(event) => {
                event.stopPropagation();
                onGameOfTheWeekClick();
              }}
            >
              {gameOfTheWeek ? "Game of the Week" : "Set as GOTW"}
            </Button>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: 48,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
              }}
            >
              <Typography variant="caption" fontWeight={800}>
                {context.primary}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {context.supporting}
              </Typography>
            </Box>
          )}
          {actionHelper && (
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              {actionHelper}
            </Typography>
          )}
        </Stack>
      </Box>

      <Stack
        spacing={0.65}
        sx={{
          px: 1.5,
          py: 1.25,
          bgcolor: "surface.low",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        {[
          [
            "League consensus",
            revealSelections && totalRevealed
              ? Math.max(homePercent, awayPercent) +
                "% " +
                (homePercent >= awayPercent
                  ? homeTeam.abbreviation
                  : awayTeam.abbreviation)
              : "Hidden until kickoff",
          ],
          [
            "Scoring line",
            showScoringLine ? formatDirectionalSpread(line) : "Off",
          ],
          [
            "Live line",
            showLiveLine ? formatDirectionalSpread(game.line) : "Off",
          ],
          ["Pick deadline", formatPickDeadline(game)],
          ["Game value", displayedGameValue],
        ].map(([label, value]) => (
          <Stack
            key={label}
            direction="row"
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="caption" fontWeight={700} textAlign="right">
              {value}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Card>
  );

  return (
    <Box
      ref={cardAnchorRef}
      data-testid="matchup-card-slot"
      sx={{
        width: 312,
        height: 576,
        flex: "0 0 auto",
        position: "relative",
        scrollSnapAlign: "start",
      }}
    >
      {!detailsOpen && cardSurface}
      {detailsOpen && overlayPosition && (
        <Portal>
          <Box
            data-testid="matchup-card-overlay"
            sx={{
              position: "absolute",
              top: overlayPosition.top,
              left: overlayPosition.left,
              width: 312,
              maxWidth: "calc(100vw - 24px)",
              overflow: "visible",
              pb: "max(12px, env(safe-area-inset-bottom))",
              borderRadius: "12px",
              zIndex: (value) => value.zIndex.appBar - 1,
            }}
          >
            <ClickAwayListener onClickAway={closeDetails}>
              <Box
                sx={{
                  width: 312,
                  maxWidth: "calc(100vw - 24px)",
                  borderRadius: "12px",
                }}
              >
                {cardSurface}
              </Box>
            </ClickAwayListener>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default MatchupCard;
